import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { myId } from "../lib/me";
import { LIBRARY_PAGE } from "../lib/builder";
import type { LibraryPick } from "../lib/weekEdit";
import { ExerciseShelf, ShelfViewSwitch, useShelfView, type PaneItem } from "./ExerciseShelf";
import { Icon } from "./Icon";
import { useCarry, type LibraryWorkout } from "./RowDrag";
import { useWeekEditor } from "./WeekEditor";
import { SegmentRadios } from "./TrainingCalendar";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * WEEK EDITOR STAGE 2 — THE COACH'S LIBRARY BESIDE THE CLIENT'S WEEK (2026-10-19, `docs/WEEK-EDITOR-STAGE2-PLAN.md` §1).
 *
 * TWO TABS, the Library's own order (Ioannis 10-19: "we need both tabs, workouts too. and exercises as we have now"):
 * WORKOUTS — the coach's templates; EXERCISES — the SAME shelf as the builder's pane (`ExerciseShelf variant="pane"`, gate
 * `w72-5-the-library`: one shelf), carried by the Week's pointer carry instead of the builder's dnd-kit drag.
 *
 * DRAG ONLY, AND EXACT (Ioannis 10-19: "we just drag the exercise or workout to where we like … if we drag between exercises
 * it will go there. same with workouts kinda, so the order … [is] as intended from the coach"). An exercise lands between the
 * two rows under the line; a workout lands between the two workouts of that day (assigned there by the page's own assign,
 * then its day order written). No `Add … to` menu here — the keyboard's path is the one each place already has: `+ Exercise`
 * under every workout and the day's `+` (drag stays an accelerator).
 * Docked beside the week from 80 rem; floating over its right edge below that, stepping aside while anything is carried.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

type Row = PaneItem & { note: string | null };
type WorkoutRow = LibraryWorkout & { exercises: number };
type Tab = "workouts" | "exercises";
const TABS: readonly Tab[] = ["workouts", "exercises"];
const TAB_LABEL: Readonly<Record<Tab, string>> = { workouts: "Workouts", exercises: "Exercises" };

export function WeekLibrary({ onClose }: { onClose: () => void }) {
  const ed = useWeekEditor();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [workouts, setWorkouts] = useState<WorkoutRow[] | null>(null);
  const [tab, setTab] = useState<Tab>("workouts");
  const [q, setQ] = useState("");
  const [view, setView] = useShelfView("builderPane");
  const { begin, overlay } = useCarry();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const me = await myId();
      /* The exercises the builder's pane offers (hidden ones left out), and the templates the Assign window offers (never a
         client copy, never an archived one). */
      const [e, w] = await Promise.all([
        supabase.from("coach_exercises").select("id, name, notes, thumbnail_url, demo_video_url")
          .eq("coach_id", me).is("hidden_at", null).order("name"),
        supabase.from("coach_workouts").select("id, name, root_workout_id, coach_workout_sections(coach_workout_exercises(id))")
          .eq("coach_id", me).is("client_id", null).is("archived_at", null).order("name"),
      ]);
      if (cancelled) return;
      setRows((e.data ?? []).map((x) => ({
        id: x.id, name: x.name, note: x.notes ?? null, thumbnailUrl: x.thumbnail_url ?? null, demoVideoUrl: x.demo_video_url ?? null,
      })));
      setWorkouts((w.data ?? []).map((x) => ({
        id: x.id, rootId: x.root_workout_id ?? x.id, name: x.name,
        exercises: (x.coach_workout_sections ?? []).reduce((n, s) => n + (s.coach_workout_exercises ?? []).length, 0),
      })));
    })();
    return () => { cancelled = true; };
  }, []);

  if (ed === null) return null;
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (name: string) => words.every((w) => name.toLowerCase().includes(w));
  const shown = (rows ?? []).filter((r) => hit(r.name));
  const shownW = (workouts ?? []).filter((r) => hit(r.name));
  const pickOf = (r: Row): LibraryPick => ({ id: r.id, name: r.name, note: r.note });
  const loaded = tab === "workouts" ? workouts : rows;
  const count = tab === "workouts" ? shownW.length : shown.length;

  return (
    <aside className="week-lib" aria-label="Your library">
      <div className="week-lib-head">
        <p className="ht-overline week-lib-title">Library</p>
        <button type="button" className="week-lib-close" aria-label="Close the library" title="Close" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <SegmentRadios name="week-lib-tab" legend="Show" className="week-lib-tabs" value={tab} onChange={setTab}
                     options={TABS.map((t) => ({ value: t, label: TAB_LABEL[t] }))} />
      <input className="ht-input week-lib-search" type="search" value={q}
             placeholder={tab === "workouts" ? "Search your workouts" : "Search your exercises"}
             aria-label={tab === "workouts" ? "Search your workouts" : "Search your exercises"}
             onChange={(e) => { setQ(e.target.value); }} />
      <div className="builder-picker-bar week-lib-bar">
        <p className="ht-overline builder-picker-count">{loaded === null ? "Loading…" : `${TAB_LABEL[tab]} (${String(count)})`}</p>
        {tab === "exercises" && <ShelfViewSwitch view={view} onChange={setView} name="week-lib-view" />}
      </div>
      {loaded !== null && count === 0 && (
        <p className="ht-caption">{q.trim() !== "" ? <>Nothing matches “{q}”.</> : tab === "workouts" ? "No workouts in your library yet." : "Your library is empty."}</p>
      )}
      <div className="week-lib-list">
        {tab === "workouts" ? (
          <ul className="week-lib-workouts">
            {shownW.slice(0, LIBRARY_PAGE).map((w) => (
              <li key={w.id} className="week-lib-workout" data-lib-workout={w.id} title="Drag onto a day"
                  onPointerDown={(e) => { begin(e, () => ({ kind: "workout", workout: { id: w.id, rootId: w.rootId, name: w.name }, words: w.name })); }}
                  onDragStart={(e) => { e.preventDefault(); }}>
                <span className="week-lib-workout-name">{w.name}</span>
                <span className="week-lib-workout-count">{w.exercises === 1 ? "1 exercise" : `${String(w.exercises)} exercises`}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ExerciseShelf variant="pane" view={view} items={shown.slice(0, LIBRARY_PAGE)} canAdd onPick={() => undefined}
            onCarry={(id, e) => {
              const r = shown.find((x) => x.id === id);
              if (r === undefined) return;
              begin(e, () => ({ kind: "library", pick: pickOf(r), words: r.name }));
            }} />
        )}
      </div>
      {overlay}
    </aside>
  );
}
